import React, { useMemo, useCallback } from 'react';
import { ChevronUp, Clock } from 'lucide-react';

/**
 * SessionJumpNav — quick-jump buttons to each --- ENCOUNTER NOTES (HH:MM) ---
 * marker in the notes textarea (Feb 26, 2026).
 *
 * Auto-hides when fewer than 2 markers exist. Tapping a timestamp scrolls the
 * textarea to that section by setting the cursor + focus — the browser then
 * auto-scrolls the selection into view.
 */
const MARKER_RE = /---\s*ENCOUNTER NOTES\s*\(([0-9]{1,2}:[0-9]{2})\)\s*---/gi;

export const findSessionMarkers = (notes) => {
  if (!notes || typeof notes !== 'string') return [];
  const out = [];
  let m;
  // Reset regex state for stateful global regex
  MARKER_RE.lastIndex = 0;
  while ((m = MARKER_RE.exec(notes)) !== null) {
    out.push({ time: m[1], index: m.index, length: m[0].length });
  }
  return out;
};

const SessionJumpNav = ({ notes, textareaRef }) => {
  const markers = useMemo(() => findSessionMarkers(notes), [notes]);

  const jumpTo = useCallback((marker) => {
    const el = textareaRef?.current;
    if (!el) return;
    try {
      el.focus({ preventScroll: false });
      // Place cursor AT the marker so the browser scrolls it into view.
      const pos = marker.index;
      el.setSelectionRange(pos, pos + marker.length);
      // For some browsers (Safari) the auto-scroll lags. Manually compute
      // an approximate scrollTop from the line number of the marker.
      const lines = notes.slice(0, pos).split('\n').length;
      const lineHeight = parseFloat(window.getComputedStyle(el).lineHeight) || 20;
      const target = Math.max(0, (lines - 3) * lineHeight);
      el.scrollTop = target;
    } catch (e) { /* selection out of range — ignore */ }
  }, [notes, textareaRef]);

  if (markers.length < 2) return null;

  return (
    <div
      className="flex items-center gap-1.5 -mt-1 px-1 py-1 text-[11px] flex-wrap"
      data-testid="session-jump-nav"
    >
      <span className="text-muted-foreground/70 inline-flex items-center gap-1 mr-0.5">
        <Clock className="w-3 h-3" />
        Jump:
      </span>
      {markers.map((m, i) => (
        <button
          key={`${m.index}-${m.time}`}
          type="button"
          onClick={() => jumpTo(m)}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-amber-300/85 hover:text-amber-200 bg-amber-500/5 hover:bg-amber-500/15 border border-amber-500/25 hover:border-amber-500/50 transition-colors font-semibold"
          data-testid={`session-jump-${m.time.replace(':', '-')}`}
          title={`Jump to session at ${m.time}`}
        >
          <ChevronUp className="w-3 h-3 opacity-70" />
          {m.time}
          {i === 0 && <span className="text-[9px] text-muted-foreground/60 ml-0.5 italic">(first)</span>}
        </button>
      ))}
    </div>
  );
};

export default SessionJumpNav;
